import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { searchClientsForPalette } from '../../services/clients';
import { reservationsOrdered } from '../../features/reservations/reservationSlice';

/**
 * Global Command Palette (Cmd+K / Ctrl+K)
 * Searches across pages, reservations, and clients
 */
export default function CommandPalette() {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [dbResults, setDbResults] = useState({ clients: [], reservations: [] });
  const [searching, setSearching] = useState(false);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  const reservations = useSelector(reservationsOrdered);


  // Static navigation items
  const PAGES = useMemo(() => [
    { type: 'page', label: 'Dashboard', path: '/dashboard', icon: 'dashboard', keywords: 'inicio home' },
    { type: 'page', label: 'Clientes', path: '/customers', icon: 'group', keywords: 'crm contactos' },
    { type: 'page', label: 'Reservas (Paquetes)', path: '/reservations', icon: 'map', keywords: 'ventas cotizaciones paquetes' },
    { type: 'page', label: 'Cruceros', path: '/cruises', icon: 'directions_boat', keywords: 'barco crucero' },
    { type: 'page', label: 'Boletos Aéreos', path: '/flights', icon: 'flight', keywords: 'vuelos tickets aereo avion' },
    { type: 'page', label: 'Destinos', path: '/destinations', icon: 'pin_drop', keywords: 'lugares ciudades paises' },
    { type: 'page', label: 'Aerolíneas', path: '/aerolines', icon: 'airlines', keywords: 'vuelos avion' },
    { type: 'page', label: 'Hoteles Configuración', path: '/hotels', icon: 'apartment', keywords: 'alojamiento' },
    { type: 'page', label: 'Excursiones', path: '/tours', icon: 'hiking', keywords: 'tours actividades' },
    { type: 'page', label: 'Proveedores', path: '/operators', icon: 'real_estate_agent', keywords: 'operadores' },
    { type: 'page', label: 'Tesorería', path: '/treasury', icon: 'account_balance_wallet', keywords: 'pagos cobros finanzas' },
    { type: 'page', label: 'Contabilidad', path: '/accounting', icon: 'receipt_long', keywords: 'facturas comprobantes' },
    { type: 'page', label: 'Mi Perfil', path: '/profile', icon: 'person', keywords: 'cuenta usuario' },
    { type: 'page', label: 'Mis Comisiones', path: '/commissions', icon: 'paid', keywords: 'ganancias' },
    { type: 'page', label: 'Staff y Usuarios', path: '/users', icon: 'manage_accounts', keywords: 'equipo asesores' },
    { type: 'page', label: 'Roles y Permisos', path: '/roles', icon: 'shield_person', keywords: 'acceso seguridad' },
    { type: 'page', label: 'Ajustes', path: '/settings', icon: 'settings', keywords: 'configuracion' },
  ], []);

  // Open/close with Cmd+K
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsOpen(prev => !prev);
      }
      if (e.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  // Focus input when open
  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setSelectedIndex(0);
      setDbResults({ clients: [], reservations: [] });
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [isOpen]);

  // Debounced search
  useEffect(() => {
    if (!query || query.length < 2) {
      setDbResults({ clients: [], reservations: [] });
      return;
    }

    const timeout = setTimeout(async () => {
      setSearching(true);
      try {
        // Search clients in Supabase via service layer
        const { data: clientResults } = await searchClientsForPalette(query);

        setDbResults({
          clients: (clientResults || []).map(c => ({
            type: 'client',
            label: c.name,
            subtitle: c.dni || c.email || '',
            path: `/customers`,
            icon: 'person',
            id: c.id
          })),
          reservations: []
        });
      } catch (e) {
        console.error('Search error:', e);
      } finally {
        setSearching(false);
      }
    }, 300);

    return () => clearTimeout(timeout);
  }, [query]);

  // Combine and filter results
  const results = useMemo(() => {
    const q = query.toLowerCase().trim();
    if (!q) return PAGES.slice(0, 6); // Show top pages when empty

    // Filter pages
    const pageResults = PAGES.filter(p =>
      p.label.toLowerCase().includes(q) || p.keywords.includes(q)
    );

    // Filter in-memory reservations
    const resResults = reservations
      .filter(r => {
        const detail = r.cotizationDetail || {};
        const name = (detail.customerName || '').toLowerCase();
        const place = (detail.place || '').toLowerCase();
        const refCode = (r.id || '').toLowerCase();
        return name.includes(q) || place.includes(q) || refCode.includes(q);
      })
      .slice(0, 5)
      .map(r => {
        const refCode = r.id ? r.id.substring(0, 8).toUpperCase() : '';
        const place = r.cotizationDetail?.place || '';
        const status = r.cotizationDetail?.status || '';
        const subtitleParts = [place, status].filter(Boolean);
        return {
          type: 'reservation',
          label: r.cotizationDetail?.customerName || `Reserva ${refCode}`,
          subtitle: `#${refCode}${subtitleParts.length ? ' · ' + subtitleParts.join(' · ') : ''}`,
          path: `/reservations/${r.id}/detail`,
          icon: 'confirmation_number',
        };
      });

    return [
      ...pageResults.map(p => ({ ...p, category: 'Páginas' })),
      ...resResults.map(r => ({ ...r, category: 'Reservas' })),
      ...dbResults.clients.map(c => ({ ...c, category: 'Clientes' })),
    ];
  }, [query, PAGES, reservations, dbResults]);

  // Keyboard navigation
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(i => Math.min(i + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && results[selectedIndex]) {
      navigate(results[selectedIndex].path);
      setIsOpen(false);
    }
  }, [results, selectedIndex, navigate]);

  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  if (!isOpen) return null;

  // Group results by category
  const grouped = {};
  results.forEach(r => {
    const cat = r.category || 'Resultados';
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(r);
  });

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[99998] animate-in fade-in duration-150"
        onClick={() => setIsOpen(false)}
      />

      {/* Palette */}
      <div className="fixed top-[15%] left-1/2 -translate-x-1/2 w-full max-w-[560px] z-[99999] px-4">
        <div className="bg-white dark:bg-boxdark rounded-2xl shadow-[0_16px_70px_-12px_rgba(0,0,0,0.3)] border border-slate-200 dark:border-strokedark overflow-hidden animate-in slide-in-from-top-3 duration-200">
          {/* Search Input */}
          <div className="flex items-center gap-3 px-5 py-4 border-b border-slate-100 dark:border-strokedark">
            <span className="material-symbols-outlined text-[22px] text-slate-400">search</span>
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Buscar páginas, clientes, reservas..."
              className="flex-1 bg-transparent text-[15px] text-[#172B4D] dark:text-white placeholder-slate-400 outline-none font-medium"
              autoComplete="off"
            />
            {searching && (
              <div className="w-4 h-4 border-2 border-slate-300 border-t-[#3245a4] rounded-full animate-spin" />
            )}
            <kbd className="hidden sm:inline-flex items-center gap-1 px-2 py-0.5 bg-slate-100 dark:bg-meta-4 text-[11px] font-mono text-slate-500 dark:text-slate-400 rounded-md border border-slate-200 dark:border-strokedark">
              ESC
            </kbd>
          </div>

          {/* Results */}
          <div className="max-h-[360px] overflow-y-auto py-2">
            {results.length === 0 && query.length >= 2 && !searching && (
              <div className="flex flex-col items-center justify-center py-10 text-slate-400 dark:text-slate-500">
                <span className="material-symbols-outlined text-[40px] mb-2">search_off</span>
                <p className="text-sm font-medium">Sin resultados para "{query}"</p>
                <p className="text-xs mt-1">Intenta con otro término</p>
              </div>
            )}

            {Object.entries(grouped).map(([category, items]) => (
              <div key={category}>
                <div className="px-5 py-1.5">
                  <span className="text-[10px] font-bold tracking-widest text-slate-400 dark:text-slate-500 uppercase">
                    {category}
                  </span>
                </div>
                {items.map((item, i) => {
                  const globalIndex = results.indexOf(item);
                  return (
                    <button
                      key={`${item.path}-${i}`}
                      className={`w-full flex items-center gap-3 px-5 py-2.5 text-left transition-colors ${
                        globalIndex === selectedIndex
                          ? 'bg-[#3245a4]/8 text-[#3245a4] dark:bg-white/5 dark:text-white'
                          : 'text-[#172B4D] dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-meta-4'
                      }`}
                      onClick={() => { navigate(item.path); setIsOpen(false); }}
                      onMouseEnter={() => setSelectedIndex(globalIndex)}
                    >
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                        globalIndex === selectedIndex
                          ? 'bg-[#3245a4]/15 text-[#3245a4] dark:bg-white/10 dark:text-white'
                          : 'bg-slate-100 dark:bg-meta-4 text-slate-500 dark:text-slate-400'
                      }`}>
                        <span className="material-symbols-outlined text-[18px]">{item.icon}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <span className="text-sm font-semibold block truncate">{item.label}</span>
                        {item.subtitle && (
                          <span className="text-[11px] text-slate-400 dark:text-slate-500 truncate block">{item.subtitle}</span>
                        )}
                      </div>
                      {globalIndex === selectedIndex && (
                        <span className="material-symbols-outlined text-[16px] text-slate-300 dark:text-slate-500">keyboard_return</span>
                      )}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between px-5 py-2.5 border-t border-slate-100 dark:border-strokedark bg-slate-50/50 dark:bg-meta-4/20">
            <div className="flex items-center gap-3 text-[11px] text-slate-400 dark:text-slate-500">
              <span className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 bg-white dark:bg-meta-4 rounded border border-slate-200 dark:border-strokedark font-mono text-[10px]">↑↓</kbd>
                Navegar
              </span>
              <span className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 bg-white dark:bg-meta-4 rounded border border-slate-200 dark:border-strokedark font-mono text-[10px]">↵</kbd>
                Abrir
              </span>
            </div>
            <span className="text-[11px] text-slate-400 dark:text-slate-500">
              {results.length} resultado{results.length !== 1 ? 's' : ''}
            </span>
          </div>
        </div>
      </div>
    </>
  );
}
